/**
 * Pályaszerkesztő – a lerakott blokkokból TrackDef-et állít elő.
 *
 * A folyamat:
 *   1. portok ellenőrzése (minden kimenet pontosan egy belépőbe csatlakozik),
 *   2. a kör bejárása a rajt/cél elemtől,
 *   3. vonal összefűzése + egyenletes újramintavételezés,
 *   4. téma szerinti díszletek, boost-padok, item-sorok és kátyúk.
 *
 * A kimenet ugyanaz a TrackDef, amit a motor, a render és a minimap használ,
 * ezért a szerkesztett pálya mindenben úgy viselkedik, mint egy beépített.
 */
import type {
  BoostPadDef,
  ItemRowDef,
  PotholeDef,
  PropDef,
  PropKind,
  TrackDef,
  TrackTheme,
  Vec2,
} from '../core/types';
import { hash01 } from '../core/math';
import { Track } from '../game/track';
import { resampleLoop } from '../data/track-geometry';
import {
  PIECES,
  entryPort,
  exitPort,
  pieceWorldPath,
  portKey,
  type EditorDoc,
  type PlacedPiece,
  type Port,
} from './model';
import { themeOf } from './themes';

export interface CompileIssue {
  level: 'error' | 'warn';
  code: string;
  message: string;
}

export interface CompileResult {
  ok: boolean;
  issues: CompileIssue[];
  def: TrackDef | null;
  /** középvonal hossza px */
  length: number;
  /** kanyar-elemek száma */
  corners: number;
  /** becsült köridő mp (340 px/s) */
  lapEstimate: number;
}

const MIN_LENGTH = 3000;

const THEME_SEED: Record<TrackTheme, number> = { city: 11, suburb: 23, lake: 37, desert: 53, circuit: 71, snow: 89 };

const round1 = (v: number): number => Math.round(v * 10) / 10;
const wrap01 = (v: number): number => ((v % 1) + 1) % 1;

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Pont a zárt sokszögön belül? (ray casting) */
const pointInPolygon = (p: Vec2, poly: Vec2[]): boolean => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
};

interface Walk {
  order: PlacedPiece[];
  closed: boolean;
  issues: CompileIssue[];
  start: PlacedPiece | null;
  openExits: Port[];
  duplicates: string[];
}

/** A blokkok bejárása a rajt/cél elemtől a kimeneti portokon keresztül. */
const walkLoop = (pieces: PlacedPiece[]): Walk => {
  const issues: CompileIssue[] = [];
  const starts = pieces.filter((p) => p.type === 'start');
  const start = starts[0] ?? null;
  const duplicates: string[] = [];
  const openExits: Port[] = [];

  if (pieces.length === 0) {
    issues.push({ level: 'error', code: 'empty', message: 'Még üres a pálya – tegyél le néhány elemet.' });
    return { order: [], closed: false, issues, start, openExits, duplicates };
  }
  if (starts.length === 0) {
    issues.push({ level: 'error', code: 'no-start', message: 'Nincs RAJT/CÉL elem. Tegyél le egyet a palettáról.' });
  } else if (starts.length > 1) {
    issues.push({ level: 'error', code: 'multi-start', message: `Csak egy RAJT/CÉL elem lehet (most ${starts.length} van).` });
  }

  // belépőportok: ide érkezhet kimenet
  const entries = new Map<string, PlacedPiece>();
  for (const piece of pieces) {
    const key = portKey(entryPort(piece));
    if (entries.has(key)) duplicates.push(key);
    else entries.set(key, piece);
  }
  if (duplicates.length > 0) {
    issues.push({
      level: 'error',
      code: 'dup-entry',
      message: `Két elem ugyanabba a portba csatlakozik (${duplicates.length} helyen).`,
    });
  }

  // kimenőportok: két kimenet ne fusson ugyanabba a belépőbe (elágazás)
  const exitKeys = new Map<string, PlacedPiece>();
  const dupExits: string[] = [];
  for (const piece of pieces) {
    const key = portKey(exitPort(piece));
    if (exitKeys.has(key)) dupExits.push(key);
    else exitKeys.set(key, piece);
  }
  if (dupExits.length > 0) {
    issues.push({
      level: 'error',
      code: 'dup-exit',
      message: `Két elem kimenete ugyanoda fut (${dupExits.length} helyen) – a kör nem ágazhat el.`,
    });
  }

  if (!start || starts.length > 1 || pieces.length === 0) {
    return { order: [], closed: false, issues, start, openExits, duplicates };
  }

  const order: PlacedPiece[] = [start];
  const seen = new Set<PlacedPiece>([start]);
  let closed = false;
  let current = start;
  for (let guard = 0; guard <= pieces.length + 1; guard++) {
    const exit = exitPort(current);
    const next = entries.get(portKey(exit));
    if (!next) {
      openExits.push(exit);
      break;
    }
    if (next === start) {
      closed = true;
      break;
    }
    if (seen.has(next)) break; // önmagába záródó hurok a rajton kívül
    seen.add(next);
    order.push(next);
    current = next;
  }

  if (!closed) {
    issues.push({
      level: 'error',
      code: 'open-loop',
      message: `Nyitott a kör: ${openExits.length > 0 ? 'a kijelölt elem kimenete nincs összekötve' : 'a kör nem ér vissza a rajt/cél elemhez'}.`,
    });
  }
  if (order.length !== pieces.length) {
    issues.push({
      level: 'error',
      code: 'disconnected',
      message: `A körbe nem kapcsolódó elem(ek): ${pieces.length - order.length} db. Minden elemnek egy zárt kör része kell legyen.`,
    });
  }
  if (!closed && pieces.length === 1) {
    issues.push({ level: 'error', code: 'one-piece', message: 'Egy elemből nem lesz kör – told meg még néhánnyal.' });
  }

  return { order, closed, issues, start, openExits, duplicates };
};

interface Decor {
  props: PropDef[];
  potholes: PotholeDef[];
}

/** Témánkénti díszletezés a pálya mentén, a belső/külső oldal felismerésével. */
const decorate = (track: Track, theme: TrackTheme): Decor => {
  const props: PropDef[] = [];
  const potholes: PotholeDef[] = [];
  const L = track.totalLength;
  const poly = track.samples.map((s) => s.pos);

  // melyik oldal van a pálya belsejében (a pozitív laterális irányhoz képest)
  const insideAt = (s: number): number => (pointInPolygon(track.posAt(s, 90), poly) ? 1 : -1);

  const add = (kind: PropKind, s: number, lateral: number, scale = 1, variant = 0): void => {
    props.push({ kind, s: wrap01(s), lateral, scale, variant });
  };
  /** a belső oldal felé eső laterális érték */
  const inside = (s: number, off: number): number => insideAt(s) * off;
  /** a külső oldal felé eső laterális érték */
  const outside = (s: number, off: number): number => -insideAt(s) * off;

  const h = (i: number, salt = 0): number => hash01(i * 92821 + salt * 31337 + THEME_SEED[theme]);

  switch (theme) {
    case 'city': {
      // házsor a külső oldalon
      for (let x = 70; x < L; x += 150) {
        const s = x / L;
        const i = Math.round(x / 150);
        const v = h(i, 1);
        if (v > 0.1) add('building', s, outside(s, 252 + v * 92), 0.8 + h(i, 2) * 0.55, Math.floor(h(i, 3) * 6));
        if (h(i, 4) > 0.72) add('tree', s + 0.002, outside(s, 200 + v * 34), 0.75 + h(i, 5) * 0.4, Math.floor(h(i, 6) * 3));
        if (h(i, 7) < 0.08) add('kiosk', s + 0.001, outside(s, 196), 0.9, Math.floor(h(i, 8) * 2));
      }
      // park a belső íven
      for (let x = 50; x < L; x += 100) {
        const s = x / L;
        const i = Math.round(x / 100);
        add('tree', s, inside(s, 170 + h(i, 11) * 54), 0.75 + h(i, 12) * 0.55, Math.floor(h(i, 13) * 3));
        if (h(i, 14) > 0.72) add('bench', s + 0.002, inside(s, 158), 0.9 + h(i, 15) * 0.3, Math.floor(h(i, 16) * 2));
        if (h(i, 17) > 0.93) add('kiosk', s + 0.001, inside(s, 170), 0.85, Math.floor(h(i, 18) * 2));
      }
      for (let x = 0; x < L; x += 150) {
        const i = Math.round(x / 150);
        add('lamp', x / L, i % 2 === 0 ? outside(x / L, 176) : inside(x / L, 176), 1, i % 3);
      }
      for (let x = 120; x < L; x += 280) {
        const s = x / L;
        const i = Math.round(x / 280);
        if (h(i, 21) < 0.34) add('hydrant', s, outside(s, 168), 1, 0);
        else if (h(i, 21) < 0.67) add('bin', s, inside(s, 168), 1, 0);
        else add('sign', s, outside(s, 182), 1, i % 4);
      }
      for (let x = 400; x < L; x += 800) add('crosswalk', x / L, 0, 1, 0);
      for (let x = 620; x < L; x += 900) {
        add('arrow', x / L, -48, 1, 0);
        add('arrow', x / L + 0.008, 48, 1, 1);
      }
      for (let x = 900; x < L; x += 1600) {
        const s = x / L;
        add('parkedCar', s, outside(s, 196), 1, Math.round(h(Math.round(x), 31) * 3));
      }
      for (let i = 0; i < 3; i++) add('puddle', 0.18 + i * 0.29, (h(i, 41) * 2 - 1) * 50, 1 + h(i, 42) * 0.5, 0);
      break;
    }

    case 'suburb': {
      for (let x = 80; x < L; x += 160) {
        const s = x / L;
        const i = Math.round(x / 160);
        add('building', s, outside(s, 262 + h(i, 1) * 110), 1.1 + h(i, 2) * 0.7, 2 + Math.floor(h(i, 3) * 4));
        if (h(i, 4) > 0.6) add('tree', s + 0.002, outside(s, 214 + h(i, 5) * 30), 0.7 + h(i, 6) * 0.4, Math.floor(h(i, 7) * 3));
      }
      for (let x = 60; x < L; x += 130) {
        const s = x / L;
        const i = Math.round(x / 130);
        if (h(i, 11) > 0.5) add('tree', s, inside(s, 178 + h(i, 12) * 40), 0.7 + h(i, 13) * 0.5, Math.floor(h(i, 14) * 3));
        if (h(i, 15) < 0.12) add('cone', s + 0.002, inside(s, 150 + h(i, 16) * 20), 1, i % 2);
      }
      for (let x = 0; x < L; x += 170) {
        const i = Math.round(x / 170);
        add('lamp', x / L, i % 2 === 0 ? outside(x / L, 186) : inside(x / L, 186), 1, i % 3);
      }
      for (let x = 140; x < L; x += 300) {
        const s = x / L;
        const i = Math.round(x / 300);
        if (h(i, 21) < 0.34) add('hydrant', s, outside(s, 174), 1, 0);
        else if (h(i, 21) < 0.62) add('bin', s, inside(s, 174), 1, 0);
        else add('sign', s, outside(s, 190), 1, i % 4);
      }
      for (let x = 300; x < L; x += 900) add('crosswalk', x / L, 0, 1, 0);
      for (let x = 500; x < L; x += 1200) {
        add('arrow', x / L, -48, 1, 0);
        add('arrow', x / L + 0.01, 48, 1, 1);
      }
      for (let i = 0; i < 2; i++) add('puddle', 0.24 + i * 0.31, (h(i, 41) * 2 - 1) * 60, 1.2 + h(i, 42) * 0.5, 0);
      for (let x = 700; x < L; x += 1400) {
        add('parkedCar', x / L, outside(x / L, 200), 1, Math.round(h(Math.round(x), 31) * 3));
      }
      break;
    }

    case 'lake': {
      // a külső oldal a víz: oda nem kerül semmi
      for (let x = 40; x < L; x += 90) {
        const s = x / L;
        const i = Math.round(x / 90);
        if (h(i, 1) > 0.12) add('tree', s, inside(s, 186 + h(i, 2) * 58), 0.8 + h(i, 3) * 0.6, Math.floor(h(i, 4) * 3));
        if (h(i, 5) > 0.66) add('bench', s + 0.002, inside(s, 160), 0.9 + h(i, 6) * 0.3, Math.floor(h(i, 7) * 2));
        if (h(i, 8) < 0.08) add('kiosk', s + 0.001, inside(s, 176), 0.9, 0);
      }
      for (let x = 0; x < L; x += 200) add('lamp', x / L, inside(x / L, 188), 1, Math.round(h(Math.round(x), 11) * 3));
      for (let x = 150; x < L; x += 300) {
        const s = x / L;
        const i = Math.round(x / 300);
        if (h(i, 21) < 0.4) add('bin', s, inside(s, 172), 1, 0);
        else if (h(i, 21) < 0.7) add('sign', s, inside(s, 184), 1, i % 4);
      }
      for (let x = 500; x < L; x += 1100) add('crosswalk', x / L, 0, 1, 0);
      for (let x = 700; x < L; x += 1500) {
        add('arrow', x / L, -48, 1, 0);
        add('arrow', x / L + 0.01, 48, 1, 1);
      }
      for (let i = 0; i < 2; i++) add('puddle', 0.3 + i * 0.35, (h(i, 41) * 2 - 1) * 40, 1.2, 0);
      for (let x = 1000; x < L; x += 1800) add('parkedCar', x / L, inside(x / L, 204), 1, Math.round(h(Math.round(x), 31) * 3));
      break;
    }

    case 'desert': {
      for (let x = 160; x < L; x += 420) {
        const s = x / L;
        const i = Math.round(x / 420);
        add('building', s, outside(s, 260 + h(i, 1) * 70), 0.8 + h(i, 2) * 0.4, Math.floor(h(i, 3) * 4));
      }
      for (let x = 60; x < L; x += 120) {
        const s = x / L;
        const i = Math.round(x / 120);
        if (h(i, 11) > 0.35) add('cactus', s, outside(s, 196 + h(i, 12) * 110), 0.8 + h(i, 13) * 0.6, Math.floor(h(i, 14) * 3));
        if (h(i, 15) < 0.3) add('rock', s + 0.002, outside(s, 210 + h(i, 16) * 120), 0.8 + h(i, 17) * 0.9, Math.floor(h(i, 18) * 4));
      }
      for (let i = 0; i < 2; i++) add('mineTower', 0.25 + i * 0.42, outside(0.25 + i * 0.42, 330), 1.1 + i * 0.2, i % 2);
      for (let x = 220; x < L; x += 500) add('sign', x / L, outside(x / L, 178), 1, Math.round(h(Math.round(x), 21) * 4));
      for (let x = 0; x < L; x += 380) {
        const i = Math.round(x / 380);
        add('lamp', x / L, i % 2 === 0 ? outside(x / L, 188) : inside(x / L, 188), 1, i % 3);
      }
      for (let x = 700; x < L; x += 1100) add('crosswalk', x / L, 0, 1, 0);
      for (let i = 0; i < 3; i++) {
        const s = 0.33 + i * 0.011;
        add('cone', s, -74 + i * 16, 1, i % 2);
      }
      add('puddle', 0.57, 34, 1.3, 0);
      for (let x = 650; x < L; x += 700) {
        const s = x / L;
        if (s < 0.06) continue;
        potholes.push({
          s: wrap01(s + (h(Math.round(x), 51) - 0.5) * 0.02),
          lateral: (h(Math.round(x), 52) * 2 - 1) * 60,
          radius: 28 + h(Math.round(x), 53) * 6,
        });
      }
      break;
    }

    case 'circuit': {
      for (let x = 350; x < L; x += 900) {
        add('grandstand', x / L, outside(x / L, 252), 1.4 + h(Math.round(x), 1) * 0.4, Math.round(h(Math.round(x), 2) * 2));
      }
      for (let x = 180; x < L; x += 450) {
        add('tyreWall', x / L, outside(x / L, 186), 1, Math.round(h(Math.round(x), 3) * 3));
      }
      for (let x = 0; x < L; x += 300) {
        const s = x / L;
        const i = Math.round(x / 300);
        add('flagpole', s, i % 2 === 0 ? outside(s, 206) : inside(s, 196), 1, i % 4);
      }
      for (let x = 150; x < L; x += 420) add('sign', x / L, outside(x / L, 176), 1, Math.round(h(Math.round(x), 4) * 4));
      for (let x = 0; x < L; x += 260) {
        const i = Math.round(x / 260);
        add('lamp', x / L, i % 2 === 0 ? outside(x / L, 178) : inside(x / L, 178), 1, i % 3);
      }
      for (let x = 200; x < L; x += 350) {
        const s = x / L;
        add('tree', s, outside(s, 320 + h(Math.round(x), 5) * 90), 0.8 + h(Math.round(x), 6) * 0.6, Math.floor(h(Math.round(x), 7) * 3));
      }
      for (let x = 400; x < L; x += 1000) add('crosswalk', x / L, 0, 1, 0);
      for (let i = 0; i < 2; i++) add('cone', 0.3 + i * 0.008, -70 + i * 16, 1, i % 2);
      break;
    }

    case 'snow': {
      // fenyves a külső oldalon, hóemberek és fenyők a belső hómezőn
      for (let x = 60; x < L; x += 120) {
        const s = x / L;
        const i = Math.round(x / 120);
        if (h(i, 1) > 0.25) add('pine', s, outside(s, 200 + h(i, 2) * 90), 0.85 + h(i, 3) * 0.5, Math.floor(h(i, 4) * 3));
        if (h(i, 5) < 0.12) add('snowman', s + 0.002, inside(s, 160 + h(i, 6) * 40), 0.9, 0);
      }
      for (let x = 40; x < L; x += 160) {
        const s = x / L;
        const i = Math.round(x / 160);
        if (h(i, 11) > 0.55) add('pine', s, inside(s, 175 + h(i, 12) * 60), 0.8 + h(i, 13) * 0.4, Math.floor(h(i, 14) * 3));
        if (h(i, 15) < 0.06) add('snowman', s + 0.001, inside(s, 150 + h(i, 16) * 30), 0.85, 0);
      }
      for (let x = 0; x < L; x += 300) {
        const i = Math.round(x / 300);
        add('lamp', x / L, i % 2 === 0 ? outside(x / L, 178) : inside(x / L, 178), 1, i % 3);
      }
      for (let x = 250; x < L; x += 600) add('sign', x / L, outside(x / L, 184), 1, Math.round(h(Math.round(x), 21) * 4));
      for (let x = 300; x < L; x += 900) add('crosswalk', x / L, 0, 1, 0);
      for (let x = 500; x < L; x += 1400) add('rock', x / L, outside(x / L, 230), 0.8, Math.round(h(Math.round(x), 31) * 4));
      add('puddle', 0.42, 32, 1.4, 0);
      break;
    }

    default:
      break;
  }

  return { props, potholes };
};

interface Features {
  boostPads: BoostPadDef[];
  itemRows: ItemRowDef[];
}

/**
 * Boost-padok és item-sorok: a kör hosszához igazított számban, a legkevésbé
 * görbülő pontokra téve, hogy ne a kanyar közepén lökjenek.
 */
const placeFeatures = (track: Track): Features => {
  const L = track.totalLength;
  const count = Math.max(3, Math.min(6, Math.round(L / 1500)));
  const flatNear = (target: number, spread: number): number => {
    let best = target;
    let bestC = Infinity;
    for (let k = -8; k <= 8; k++) {
      const s = target + (k / 8) * spread;
      const c = Math.abs(track.curvatureAt(s));
      if (c < bestC) {
        bestC = c;
        best = s;
      }
    }
    return wrap01(best);
  };

  const boostPads: BoostPadDef[] = [];
  const itemRows: ItemRowDef[] = [];
  for (let i = 0; i < count; i++) {
    const base = (i + 0.5) / count;
    const boostS = flatNear(base - 0.035, 0.06);
    const itemS = flatNear(base + 0.035, 0.06);
    boostPads.push({ s: boostS, lateral: 0, length: 250, width: 150 });
    itemRows.push({ s: itemS, lateral: 0, count: 4, spacing: 54 });
  }
  return { boostPads, itemRows };
};

/** Öntávolság: a középvonal két, egymástól távoli szakasza ne fusson egymásra. */
const minSelfDistance = (track: Track): number => {
  const s = track.samples;
  let min = Infinity;
  for (let i = 0; i < s.length; i += 2) {
    for (let j = i + 2; j < s.length; j += 2) {
      // csak a pálya mentén legalább 800 px-re lévő pontokat hasonlítjuk:
      // egy kanyar két vége természetesen közel van egymáshoz
      const sGap = Math.abs(s[j].s - s[i].s);
      const lapGap = Math.min(sGap, track.totalLength - sGap);
      if (lapGap < 800) continue;
      const d = Math.hypot(s[i].pos.x - s[j].pos.x, s[i].pos.y - s[j].pos.y);
      if (d < min) min = d;
    }
  }
  return min;
};

const countCorners = (pieces: PlacedPiece[]): number =>
  pieces.filter((p) => PIECES[p.type].group === 'curve' || PIECES[p.type].group === 'special').length;

export interface PreviewChain {
  /** világkoordinátás vonal, a lánc elejétől a végéig */
  points: Vec2[];
  /** zárt kör (az utolsó pont az elsőhöz csatlakozik) */
  closed: boolean;
}

/**
 * A lerakott elemekből álló összefüggő vonalak – a szerkesztő ezt rajzolja ki,
 * nem elemenként. Így nincsenek átfedő vonalvégek a csatlakozásoknál.
 */
export const previewChains = (pieces: PlacedPiece[]): PreviewChain[] => {
  if (pieces.length === 0) return [];
  const byEntry = new Map<string, PlacedPiece>();
  for (const piece of pieces) byEntry.set(portKey(entryPort(piece)), piece);
  const nextOf = new Map<string, PlacedPiece | null>();
  const fed = new Set<string>();
  for (const piece of pieces) {
    const exit = exitPort(piece);
    fed.add(portKey(exit));
    nextOf.set(piece.uid, byEntry.get(portKey(exit)) ?? null);
  }

  const visited = new Set<string>();
  const build = (first: PlacedPiece): PreviewChain => {
    const order: PlacedPiece[] = [];
    let current: PlacedPiece | null = first;
    let closed = false;
    while (current && !visited.has(current.uid)) {
      visited.add(current.uid);
      order.push(current);
      const next: PlacedPiece | null = nextOf.get(current.uid) ?? null;
      if (next === first) {
        closed = true;
        break;
      }
      current = next;
    }
    const points: Vec2[] = [];
    for (const piece of order) {
      const pts = pieceWorldPath(piece);
      if (points.length === 0) points.push(...pts);
      else points.push(...pts.slice(1));
    }
    if (closed && points.length > 2) points.pop();
    return { points, closed };
  };

  const chains: PreviewChain[] = [];
  // először a valódi láncvégekről (amelyek belépőjét nem köti be semmi)
  for (const piece of pieces) {
    if (visited.has(piece.uid)) continue;
    if (fed.has(portKey(entryPort(piece)))) continue;
    chains.push(build(piece));
  }
  // a maradék zárt hurkok
  for (const piece of pieces) {
    if (visited.has(piece.uid)) continue;
    chains.push(build(piece));
  }
  return chains;
};

/** Egy vonal újramintavételezése egyenletes közön (az átfedésvizsgálathoz). */
export const samplePolyline = (points: Vec2[], spacing = 20): Vec2[] => {
  if (points.length < 2) return points.map((p) => ({ ...p }));
  const out: Vec2[] = [{ ...points[0] }];
  let since = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len <= 0.0001) continue;
    let t = 0;
    while (since + (len - t) >= spacing) {
      t += spacing - since;
      since = 0;
      out.push({ x: a.x + ((b.x - a.x) * t) / len, y: a.y + ((b.y - a.y) * t) / len });
    }
    since += len - t;
  }
  const last = points[points.length - 1];
  const tail = out[out.length - 1];
  if (Math.hypot(last.x - tail.x, last.y - tail.y) > 1) out.push({ ...last });
  return out;
};

const pointSegmentDistance = (p: Vec2, a: Vec2, b: Vec2): number => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby || 1;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2));
  return Math.hypot(p.x - (a.x + abx * t), p.y - (a.y + aby * t));
};

/** Két vonal legkisebb távolsága (az első vonal mintapontjaiból). */
export const polylineDistance = (from: Vec2[], to: Vec2[]): number => {
  let best = Infinity;
  for (const p of from) {
    for (let i = 1; i < to.length; i++) {
      const d = pointSegmentDistance(p, to[i - 1], to[i]);
      if (d < best) best = d;
    }
  }
  return best;
};

/**
 * A jelölt elem láncszomszédai (2 elem mélységig). A közvetlen szomszédokat
 * nem vizsgáljuk átfedésre: azok a csatlakozás miatt természetesen közel vannak.
 */
const chainNeighbours = (pieces: PlacedPiece[], candidate: PlacedPiece, depth = 2): Set<string> => {
  const byEntry = new Map<string, PlacedPiece>();
  const byExit = new Map<string, PlacedPiece>();
  for (const piece of pieces) {
    byEntry.set(portKey(entryPort(piece)), piece);
    byExit.set(portKey(exitPort(piece)), piece);
  }
  const near = new Set<string>([candidate.uid]);
  const walk = (from: PlacedPiece, forward: boolean, left: number): void => {
    if (left <= 0) return;
    const next = forward ? byEntry.get(portKey(exitPort(from))) : byExit.get(portKey(entryPort(from)));
    if (!next || near.has(next.uid)) return;
    near.add(next.uid);
    walk(next, forward, left - 1);
  };
  walk(candidate, true, depth);
  walk(candidate, false, depth);
  return near;
};

/**
 * Egy vonalban, legfeljebb 2 cella réssel álló elemek (hézag kitöltése) – ezek
 * nem átfedés, hanem ugyanannak az útnak a folytatásai.
 */
const gapNeighbours = (pieces: PlacedPiece[], candidate: PlacedPiece): Set<string> => {
  const near = new Set<string>();
  const dirVec = (d: number): Vec2 => [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 }, { x: 0, y: -1 }][d] ?? { x: 1, y: 0 };
  const entry = entryPort(candidate);
  const exit = exitPort(candidate);
  const ahead = dirVec(exit.dir);
  const behind = dirVec(entry.dir);
  for (const piece of pieces) {
    const e = entryPort(piece);
    if (e.dir === exit.dir) {
      const dx = e.x - exit.x;
      const dy = e.y - exit.y;
      const along = dx * ahead.x + dy * ahead.y;
      const cross = dx * ahead.y - dy * ahead.x;
      if (along > 0 && along <= 2 && Math.abs(cross) < 0.001) near.add(piece.uid);
    }
    const x = exitPort(piece);
    if (x.dir === entry.dir) {
      const dx = x.x - entry.x;
      const dy = x.y - entry.y;
      const along = dx * behind.x + dy * behind.y;
      const cross = dx * behind.y - dy * behind.x;
      if (along < 0 && along >= -2 && Math.abs(cross) < 0.001) near.add(piece.uid);
    }
  }
  return near;
};

/**
 * A jelölt elem útja átfed-e egy másik elemmel? A csatlakozó szomszédokat
 * kihagyjuk; minden más elemnél a két középvonal legalább `minGap` távolságot kell
 * tartson (két párhuzamos út közt ennyi kell, hogy a járda ne lógjon egymásra).
 */
export const overlapsSomething = (pieces: PlacedPiece[], candidate: PlacedPiece, minGap: number): boolean => {
  if (pieces.length === 0) return false;
  const near = chainNeighbours(pieces, candidate);
  for (const uid of gapNeighbours(pieces, candidate)) near.add(uid);
  const probe = samplePolyline(pieceWorldPath(candidate), 18);
  for (const other of pieces) {
    if (near.has(other.uid)) continue;
    if (polylineDistance(probe, pieceWorldPath(other)) < minGap) return true;
  }
  return false;
};

/** Blokkokból TrackDef. Hibás körnél `def: null`, de a hibaüzenetek használhatók. */
export const compileDoc = (doc: EditorDoc): CompileResult => {
  const theme = themeOf(doc.theme);
  const walk = walkLoop(doc.pieces);
  const issues = [...walk.issues];

  if (issues.some((i) => i.level === 'error')) {
    return { ok: false, issues, def: null, length: 0, corners: countCorners(doc.pieces), lapEstimate: 0 };
  }

  // vonal összefűzése: minden blokk a saját belépőjétől a kimenetéig
  const raw: Vec2[] = [];
  for (const piece of walk.order) {
    const pts = pieceWorldPath(piece);
    if (raw.length === 0) raw.push(...pts);
    else raw.push(...pts.slice(1));
  }
  if (raw.length >= 2 && dist(raw[0], raw[raw.length - 1]) < 0.5) raw.pop();
  if (raw.length < 6) {
    issues.push({ level: 'error', code: 'too-few-points', message: 'Túl kevés elem a körhöz.' });
    return { ok: false, issues, def: null, length: 0, corners: countCorners(doc.pieces), lapEstimate: 0 };
  }

  // egyenletes pontköz: a Catmull-Rom így szépen simul, nem lő túl a csatlakozásoknál
  const centerline = resampleLoop(raw, 32);
  if (centerline.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
    issues.push({ level: 'error', code: 'nan', message: 'Hibás geometria (NaN) – töröld az utolsó elemeket.' });
    return { ok: false, issues, def: null, length: 0, corners: countCorners(doc.pieces), lapEstimate: 0 };
  }

  const name = doc.name.trim() || 'Saját pálya';
  const base: TrackDef = {
    id: doc.id,
    name,
    subtitle: `${theme.label} · 3 kör`,
    description: `Saját pálya a szerkesztőből: ${theme.label.toLowerCase()} téma, ${Math.round(centerline.length)} vezérlőpont.`,
    centerline,
    roadHalfWidth: theme.roadHalfWidth,
    kerbWidth: theme.kerbWidth,
    sidewalkWidth: theme.sidewalkWidth,
    startS: 0,
    laps: 3,
    theme: doc.theme,
    props: [],
    boostPads: [],
    itemRows: [],
    ambient: { ...theme.ambient },
    cameraZoom: theme.cameraZoom,
    custom: true,
    rev: doc.updatedAt,
  };

  let track: Track;
  try {
    track = new Track(base);
  } catch (error) {
    issues.push({
      level: 'error',
      code: 'geometry',
      message: error instanceof Error ? error.message : 'A geometria nem építhető fel.',
    });
    return { ok: false, issues, def: null, length: 0, corners: countCorners(doc.pieces), lapEstimate: 0 };
  }

  if (track.totalLength < MIN_LENGTH) {
    issues.push({
      level: 'error',
      code: 'too-short',
      message: `Túl rövid a kör (${Math.round(track.totalLength)} px). Legalább ${MIN_LENGTH} px kell – told meg egyenesekkel.`,
    });
    return { ok: false, issues, def: null, length: track.totalLength, corners: countCorners(doc.pieces), lapEstimate: 0 };
  }

  const gap = minSelfDistance(track);
  if (gap < track.corridorHalf * 2 + 12) {
    issues.push({
      level: 'warn',
      code: 'self-close',
      message: `A pálya két szakasza túl közel fut egymáshoz (${Math.round(gap)} px). A vezetés ott összezavarodhat.`,
    });
  }

  const decor = decorate(track, doc.theme);
  const features = placeFeatures(track);

  const def: TrackDef = {
    ...base,
    description: `Saját pálya: ${Math.round(track.totalLength)} px, ${countCorners(doc.pieces)} kanyar, kb. ${Math.round(
      track.totalLength / 340,
    )} mp/kör.`,
    props: decor.props,
    potholes: decor.potholes,
    boostPads: features.boostPads,
    itemRows: features.itemRows,
  };

  return {
    ok: true,
    issues,
    def,
    length: track.totalLength,
    corners: countCorners(doc.pieces),
    lapEstimate: round1(track.totalLength / 340),
  };
};

/** A szerkesztő státuszsorához: rövid, emberi összefoglaló. */
export const summaryOf = (result: CompileResult): string => {
  if (!result.ok) return 'A kör még nem zárt.';
  return `${Math.round(result.length)} px · ${result.corners} kanyar · ~${Math.round(result.lapEstimate)} mp/kör`;
};
